import { Injectable, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from './api.service';

export interface AdminUser {
  id: string;
  email: string;
  name?: string;
  role: string;
  status?: string;
  role_name?: string;
  role_badge?: string;
  role_color?: string;
  permissions?: string[];
  last_login_at?: string;
}

interface SessionData {
  user: AdminUser;
  token: string;
}

const STORAGE_KEY = 'scs_admin_session';

const DEFAULT_ROLE_PERMISSIONS: Record<string, string[]> = {
  super_admin: ['*'],
  campaign_manager: [
    'v_list',
    'c_list',
    'c_create',
    'c_golive',
    'c_scans',
    'cnt_events',
    'cnt_banners',
    'cnt_stamps',
    'cnt_media',
    'r_list',
    'a_view',
  ],
  content_editor: [
    'c_list',
    'c_create',
    'cnt_events',
    'cnt_banners',
    'cnt_stamps',
    'cnt_media',
  ],
  rewards_manager: [
    'v_list',
    'c_list',
    'c_scans',
    'cnt_media',
    'r_list',
    'r_create',
    'r_codes_gen',
    'r_codes_revoke',
    'r_redeem',
    'a_view',
  ],
  counter_staff: [
    'r_list',
    'r_redeem',
  ],
  dpo: [
    'v_list',
    'v_profile',
    'v_export',
    'v_delete',
    'v_consent_withdraw',
    'a_view',
    'a_export',
  ],
  viewer: [
    'v_list',
    'c_list',
    'c_scans',
    'r_list',
    'a_view',
  ],
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private router = inject(Router);
  private api = inject(ApiService);
  private sessionState = signal<SessionData | null>(this.readStorage());

  readonly currentUser = computed(() => this.sessionState()?.user ?? null);
  readonly isAuthenticated = computed(() => !!this.sessionState()?.token);

  get isLoggedIn(): boolean {
    return this.isAuthenticated();
  }

  get token(): string | null {
    return this.sessionState()?.token ?? null;
  }

  get user(): AdminUser | null {
    return this.sessionState()?.user ?? null;
  }

  get email(): string {
    return this.user?.email || '';
  }

  get role(): string {
    return this.user?.role || 'super_admin';
  }

  get roleBadge(): string {
    return this.user?.role_badge || this.user?.role_name || this.role;
  }

  get roleColor(): string {
    return this.user?.role_color || '#2563eb';
  }

  get isSuperAdmin(): boolean {
    return this.role === 'super_admin';
  }

  get effectivePermissions(): string[] {
    if (this.isSuperAdmin) return ['*'];
    const custom = this.user?.permissions;
    if (Array.isArray(custom) && custom.length > 0) {
      return custom;
    }
    return DEFAULT_ROLE_PERMISSIONS[this.role] || [];
  }

  hasPermission(permId: string): boolean {
    if (!this.isLoggedIn) return false;
    if (this.isSuperAdmin) return true;
    const perms = this.effectivePermissions;
    if (perms.includes('*')) return true;
    return perms.includes(permId);
  }

  hasAnyPermission(permIds: string[]): boolean {
    if (!this.isLoggedIn) return false;
    if (this.isSuperAdmin) return true;
    const perms = this.effectivePermissions;
    if (perms.includes('*')) return true;
    return permIds.some(id => perms.includes(id));
  }

  hasAllPermissions(permIds: string[]): boolean {
    if (!this.isLoggedIn) return false;
    if (this.isSuperAdmin) return true;
    const perms = this.effectivePermissions;
    if (perms.includes('*')) return true;
    return permIds.every(id => perms.includes(id));
  }

  hasModuleAccess(modulePrefix: string): boolean {
    if (!this.isLoggedIn) return false;
    if (this.isSuperAdmin) return true;
    const perms = this.effectivePermissions;
    if (perms.includes('*')) return true;
    return perms.some(p => p.startsWith(modulePrefix));
  }

  isTokenExpired(tokenStr?: string | null): boolean {
    const t = tokenStr || this.token;
    if (!t) return true;
    try {
      const parts = t.split('.');
      if (parts.length < 2) return false;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (payload && payload.exp) {
        const now = Math.floor(Date.now() / 1000);
        return payload.exp <= now;
      }
      return false;
    } catch {
      return false;
    }
  }

  checkSession(): boolean {
    const session = this.readStorage();
    if (!session || !session.token) {
      if (this.sessionState()) {
        this.sessionState.set(null);
      }
      return false;
    }

    if (this.isTokenExpired(session.token)) {
      this.handleSessionExpired('Your session has expired. Please log in again.');
      return false;
    }

    return true;
  }

  handleSessionExpired(message: string = 'Session expired. Please log in again.') {
    localStorage.removeItem(STORAGE_KEY);
    this.sessionState.set(null);
    this.router.navigate(['/login'], {
      queryParams: { session_expired: '1' }
    });
  }

  private readStorage(): SessionData | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed?.token && this.isTokenExpired(parsed.token)) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  async login(email: string, password: string, turnstileToken?: string): Promise<boolean> {
    try {
      const payload: any = { email, password };
      if (turnstileToken) {
        payload.turnstileToken = turnstileToken;
      }
      const res = await this.api.post<any>('/admin/auth/login', payload);
      if (res?.token && res?.user) {
        const session: SessionData = { user: res.user, token: res.token };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
        this.sessionState.set(session);
        return true;
      }
    } catch (e: any) {
      console.warn('Backend login failed:', e);
      throw e;
    }
    return false;
  }

  async refreshUser(): Promise<AdminUser | null> {
    try {
      if (!this.sessionState()?.token) return null;
      const res = await this.api.get<{ user: AdminUser }>('/admin/auth/me');
      if (res?.user) {
        const session: SessionData = {
          user: res.user,
          token: this.sessionState()!.token,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
        this.sessionState.set(session);
        return res.user;
      }
    } catch (e: any) {
      if (e?.status === 401) {
        this.handleSessionExpired();
      }
    }
    return null;
  }

  logout() {
    localStorage.removeItem(STORAGE_KEY);
    this.sessionState.set(null);
    this.router.navigate(['/login']);
  }
}

